import type { TypedSupabaseClient } from '../client';
import type { TablesUpdate } from '../../../supabase-client/src/database.types';
import type {
  IRepresentativeRepository,
  Representative,
  RepresentativeFilters,
  CreateRepresentativeInput,
  UpdateRepresentativeInput,
  Client,
} from '../../../interfaces/src';

export class SupabaseRepresentativeRepository implements IRepresentativeRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: RepresentativeFilters): Promise<Representative[]> {
    let query = this.supabase.from('representatives').select('*');
    if (filters?.search) {
      query = query.or(
        `name.ilike.%${filters.search}%,phone.ilike.%${filters.search}%,email.ilike.%${filters.search}%`,
      );
    }
    const { data, error } = await query.order('name');
    if (error) throw error;
    return (data ?? []).map(mapRepresentative);
  }

  async getById(id: string): Promise<Representative | null> {
    const { data, error } = await this.supabase
      .from('representatives')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapRepresentative(data);
  }

  async create(input: CreateRepresentativeInput): Promise<Representative> {
    const { data, error } = await this.supabase
      .from('representatives')
      .insert({
        name: input.name,
        phone: input.phone ?? null,
        email: input.email ?? null,
        notes: input.notes ?? null,
        commission_rate: input.commissionRate ?? 0,
      })
      .select()
      .single();
    if (error) throw error;
    return mapRepresentative(data);
  }

  async update(id: string, input: UpdateRepresentativeInput): Promise<Representative> {
    const mapped: TablesUpdate<'representatives'> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.phone !== undefined) mapped['phone'] = input.phone || null;
    if (input.email !== undefined) mapped['email'] = input.email || null;
    if (input.notes !== undefined) mapped['notes'] = input.notes || null;
    if (input.commissionRate !== undefined) mapped['commission_rate'] = input.commissionRate;

    const { data, error } = await this.supabase
      .from('representatives')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapRepresentative(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('representatives').delete().eq('id', id);
    if (error) throw error;
  }

  async listByClient(clientId: string): Promise<Representative[]> {
    // Two-step fetch avoids relying on auto-detected FK relationships in the
    // Supabase client's typed output, which doesn't know our hand-maintained
    // `client_representatives` schema.
    const { data: links, error: linksError } = await this.supabase
      .from('client_representatives')
      .select('representative_id')
      .eq('client_id', clientId);
    if (linksError) throw linksError;
    const ids = (links ?? []).map((l) => l['representative_id'] as string);
    if (ids.length === 0) return [];

    const { data: reps, error: repsError } = await this.supabase
      .from('representatives')
      .select('*')
      .in('id', ids);
    if (repsError) throw repsError;
    return (reps ?? []).map(mapRepresentative).sort((a, b) => a.name.localeCompare(b.name));
  }

  async listClientsOf(representativeId: string): Promise<Client[]> {
    const { data: links, error: linksError } = await this.supabase
      .from('client_representatives')
      .select('client_id')
      .eq('representative_id', representativeId);
    if (linksError) throw linksError;
    const ids = (links ?? []).map((l) => l['client_id'] as string);
    if (ids.length === 0) return [];

    const { data: clients, error: clientsError } = await this.supabase
      .from('clients')
      .select('*')
      .in('id', ids);
    if (clientsError) throw clientsError;
    return (clients ?? []).map(mapClient).sort((a, b) => a.name.localeCompare(b.name));
  }

  async linkClient(clientId: string, representativeId: string): Promise<void> {
    const { error } = await this.supabase
      .from('client_representatives')
      .insert({ client_id: clientId, representative_id: representativeId });
    // 23505 = duplicate PK = already linked, safe to swallow.
    if (error && (error as { code?: string }).code !== '23505') throw error;
  }

  async unlinkClient(clientId: string, representativeId: string): Promise<void> {
    const { error } = await this.supabase
      .from('client_representatives')
      .delete()
      .eq('client_id', clientId)
      .eq('representative_id', representativeId);
    if (error) throw error;
  }
}

function mapRepresentative(row: Record<string, unknown>): Representative {
  return {
    id: row['id'] as string,
    name: row['name'] as string,
    phone: (row['phone'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    commissionRate: Number(row['commission_rate'] ?? 0),
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapClient(row: Record<string, unknown>): Client {
  return {
    id: row['id'] as string,
    code: row['code'] as string,
    name: row['name'] as string,
    type: row['type'] as Client['type'],
    taxId: (row['tax_id'] as string) ?? null,
    address: (row['address'] as string) ?? null,
    city: (row['city'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    fax: (row['fax'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    carRegistrationNumber: (row['car_registration_number'] as string) ?? null,
    defaultDiscountPct: (row['default_discount_pct'] as number) ?? 0,
    paymentTermsDays: (row['payment_terms_days'] as number) ?? 0,
    notes: (row['notes'] as string) ?? null,
    isDefault: (row['is_default'] as boolean) ?? false,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
